import { EquipmentTypeBadge } from '@/components/badges/EquipmentTypeBadge';
import { EquipmentTypeScopeBadge } from '@/components/badges/EquipmentTypeScopeBadge';
import { EquipmentTypeSelect } from '@/components/EquipmentTypeSelect';
import { FieldDataTypeBadge } from '@/components/badges/FieldDataTypeBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import { allEquipmentTypesState, appEquipmentConfigState } from '@/state';
import { useAsyncAtomValue, useLoadableAtomValue } from '@/state/loadableHooks';
import logger from '@/util/logger';
import { zodResolver } from '@hookform/resolvers/zod';
import { AppDao } from '@rock/db-admin';
import type { AppEquipmentConfig, CoreField } from '@rock/types';
import { EquipmentTypeIdSchema } from '@rock/types';
import { type FC, useEffect, useMemo } from 'react';
import { type UseFormReturn, useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as z from 'zod';

const log = logger('CoreFieldsPage');
const appDao = new AppDao();

// The page edits two properties per field: the enabled flag and the type list. A core field's
// definition — label, help text, data type, scopes, and its position in the form — is owned by the
// migration that registers it, so the form carries only these and merges them back onto whatever the
// doc holds at save time.
const FormSchema = z.object({
    fields: z.array(
        z.object({
            dataKey: z.string(),
            enabled: z.boolean(),
            /** Empty = every type (the stored field is then absent). */
            equipmentTypeIds: z.array(z.string()),
        }),
    ),
});

type FormValues = z.infer<typeof FormSchema>;

function toFormValues(config: AppEquipmentConfig | undefined): FormValues {
    // An absent flag means enabled, here as everywhere else.
    return {
        fields: (config?.coreFields ?? []).map((f) => ({
            dataKey: f.dataKey,
            enabled: f.enabled !== false,
            equipmentTypeIds: f.equipmentTypeIds ?? [],
        })),
    };
}

function applyEdits(config: AppEquipmentConfig, values: FormValues): AppEquipmentConfig['coreFields'] {
    const edits = new Map(values.fields.map((f) => [f.dataKey, f]));
    return config.coreFields.map((f) => {
        const edit = edits.get(f.dataKey);
        const { equipmentTypeIds: stored, ...rest } = f;
        const typeIds = edit ? edit.equipmentTypeIds : (stored ?? []);
        return {
            ...rest,
            enabled: edit?.enabled ?? f.enabled !== false,
            ...(typeIds.length ? { equipmentTypeIds: EquipmentTypeIdSchema.array().parse(typeIds) } : {}),
        };
    });
}

export const CoreFieldsPage: FC = () => {
    const [config, configLoadable] = useLoadableAtomValue(appEquipmentConfigState, undefined);

    const form = useForm<FormValues, unknown, FormValues>({
        resolver: zodResolver(FormSchema),
        defaultValues: toFormValues(undefined),
    });

    // Rows render from the FORM array, not from the config, so a switch is never bound to an index
    // the form hasn't seeded yet (which would paint every field as disabled) and never to a different
    // field than the row it sits on when the live doc gains one mid-edit. Definitions are looked up
    // by dataKey below.
    const rows = useFieldArray({ control: form.control, name: 'fields' });
    // Keyed by plain string: a form row's `dataKey` has lost the DataKey brand by the time it
    // round-trips through the schema, and re-branding it here would assert something unchecked.
    const definitions = new Map<string, CoreField>((config?.coreFields ?? []).map((f) => [f.dataKey, f]));

    // Seed once the config resolves (defaultValues capture before the atom settles); never clobber
    // in-progress edits.
    useEffect(() => {
        if (config && !form.formState.isDirty) {
            form.reset(toFormValues(config));
        }
    }, [config, form]);

    const onSubmit = async (values: FormValues) => {
        if (!config) return;
        try {
            await appDao.setCoreFields(applyEdits(config, values));
            toast.success('Core fields saved');
            form.reset(values);
        } catch (err) {
            log.error('Failed to save core fields', { err });
            toast.error('Failed to save core fields');
        }
    };

    return (
        <div className="p-6 max-w-3xl">
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Core Fields</CardTitle>
                            <CardDescription>
                                The fields offered for every piece of equipment, ahead of the type's own input fields.
                                Which fields exist — and their labels, help text and order — is set by a migration; this
                                page turns them on and off, and limits a field to the catalog types it applies to (no
                                types listed = every type). A disabled field is still stored here but reaches no form,
                                guide node or detail row, so a new field can ship dark and be switched on when it's
                                ready. Only app builds that ship the filter honor the switch: homes on an older build
                                see every field registered here, disabled or not.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            {rows.fields.map((row, index) => {
                                const field = definitions.get(row.dataKey);
                                return field ? (
                                    <CoreFieldRow key={row.id} index={index} field={field} form={form} />
                                ) : null;
                            })}
                            {configLoadable.state === 'loading' && (
                                <div className="text-sm text-muted-foreground">Loading…</div>
                            )}
                            {configLoadable.state === 'hasError' && (
                                <div className="text-sm text-destructive">
                                    Could not load the equipment config. Check that `app/equipment-config` parses.
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Button type="submit" disabled={!config || !form.formState.isDirty || form.formState.isSubmitting}>
                        {form.formState.isSubmitting ? 'Saving...' : 'Save Core Fields'}
                    </Button>
                </form>
            </Form>
        </div>
    );
};

type CoreFieldRowProps = {
    index: number;
    field: CoreField;
    form: UseFormReturn<FormValues, unknown, FormValues>;
};

const CoreFieldRow: FC<CoreFieldRowProps> = ({ index, field, form }) => {
    const enabled = form.watch(`fields.${index}.enabled`);
    const typeIds = form.watch(`fields.${index}.equipmentTypeIds`);
    const types = useAsyncAtomValue(allEquipmentTypesState, []);
    const typeTitles = useMemo(() => new Map(types.map((t) => [t.id, t.title])), [types]);
    const selected = EquipmentTypeIdSchema.array().parse(typeIds);
    const setTypeIds = (next: string[]) =>
        form.setValue(`fields.${index}.equipmentTypeIds`, next, { shouldDirty: true });

    return (
        <div className="border rounded-lg p-4 flex items-start gap-4">
            <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{field.label}</span>
                    <code className="text-xs text-muted-foreground">{field.dataKey}</code>
                    <FieldDataTypeBadge
                        dataType={field.dataType}
                        dimensionsSubType={field.dimensionsSubType}
                        defaultLengthSelection={field.defaultLengthSelection}
                        options={field.options}
                    />
                    {field.equipmentScopes?.map((scope) => (
                        <EquipmentTypeScopeBadge key={scope} scope={scope} />
                    ))}
                    {!enabled && <Badge variant="secondary">Disabled</Badge>}
                </div>
                {field.helpText && <div className="text-sm text-muted-foreground">{field.helpText}</div>}
                <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">
                        {selected.length ? 'Offered for:' : 'Offered for every type.'}
                    </span>
                    {selected.map((typeId) => (
                        <EquipmentTypeBadge
                            key={typeId}
                            typeId={typeId}
                            title={typeTitles.get(typeId)}
                            onRemove={() => setTypeIds(selected.filter((id) => id !== typeId))}
                        />
                    ))}
                    <EquipmentTypeSelect
                        selected={selected}
                        allowGroups
                        onPick={(ids) => setTypeIds([...selected, ...ids.filter((id) => !selected.includes(id))])}
                    />
                </div>
            </div>
            <FormField
                control={form.control}
                name={`fields.${index}.enabled`}
                render={({ field: flag }) => (
                    <FormItem>
                        <FormControl>
                            <Switch checked={flag.value} onCheckedChange={flag.onChange} />
                        </FormControl>
                    </FormItem>
                )}
            />
        </div>
    );
};
